import { Component, Inject, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VideoConfig, VideoItemConfig } from 'src/app/core/models/contract';
import { SignatureVideoPlayerComponent } from '../signature-video-player/signature-video-player.component';
import { EventService } from 'src/app/core/services/event.service';
import { EventTypes } from 'src/app/core/models/events.model';

export interface VideoModalData {
  video: VideoItemConfig;
  autoplay?: boolean;
  controls?: VideoConfig['controls'];
  storageKey: string;
  claimId: string;
  videoIndex: number;
}

export interface VideoModalResult {
  completed: boolean;
  progress: number;
  currentTime: number;
}

@Component({
  selector: 'app-video-modal',
  templateUrl: './video-modal.component.html',
  styleUrls: ['./video-modal.component.scss'],
})
export class VideoModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('player') player?: SignatureVideoPlayerComponent;

  playerConfig: VideoConfig;
  startTime = 0;

  private progressInterval?: ReturnType<typeof setInterval>;
  private lastProgress = 0;
  private lastCurrentTime = 0;

  /** Tracks last currentTime snapshot used for 30s heartbeat */
  private lastHeartbeatTime = 0;

  constructor(
    public dialogRef: MatDialogRef<VideoModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VideoModalData,
    private eventService: EventService
  ) {
    this.playerConfig = {
      url: data.video.url,
      unskippable: data.video.unskippable,
      autoplay: true,
      controls: data.controls,
    };

    // Load saved progress from localStorage
    const saved = this.loadProgress();
    if (saved && !saved.completed) {
      this.startTime = saved.currentTime;
      this.lastProgress = saved.progress;
      this.lastCurrentTime = saved.currentTime;
      this.lastHeartbeatTime = saved.currentTime;
    }

    // Handle backdrop click → close with progress
    dialogRef.backdropClick().subscribe(() => {
      this.closeWithProgress();
    });

    dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.closeWithProgress();
      }
    });
  }

  ngAfterViewInit(): void {
    this.progressInterval = setInterval(() => {
      if (this.player) {
        this.lastProgress = this.player.progress;
        this.lastCurrentTime = this.player.currentTime;
        this.saveProgress(false);

        // Fire heartbeat every ~30s of video time watched
        if (this.lastCurrentTime - this.lastHeartbeatTime >= 30) {
          this.lastHeartbeatTime = this.lastCurrentTime;
          this.trackEvent(EventTypes.VIDEO_PROGRESS_HEARTBEAT, {
            progress_pct: Math.round(this.lastProgress),
            current_time_seconds: Math.round(this.lastCurrentTime),
            video_duration_seconds: Math.round(this.player.duration ?? 0),
          });
        }
      }
    }, 2000);
  }

  ngOnDestroy(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  }

  onVideoStarted(): void {
    this.trackEvent(EventTypes.VIDEO_START, {
      current_time_seconds: 0,
      video_duration_seconds: Math.round(this.player?.duration ?? 0),
    });
  }

  onVideoPaused(): void {
    this.trackEvent(EventTypes.VIDEO_PAUSE, {
      progress_pct: Math.round(this.lastProgress),
      current_time_seconds: Math.round(this.lastCurrentTime),
    });
  }

  onVideoResumed(): void {
    this.trackEvent(EventTypes.VIDEO_RESUME, {
      progress_pct: Math.round(this.lastProgress),
      current_time_seconds: Math.round(this.lastCurrentTime),
    });
  }

  onVideoCompleted(): void {
    this.trackEvent(EventTypes.VIDEO_COMPLETED, {
      video_duration_seconds: Math.round(this.player?.duration ?? 0),
    });
    this.saveProgress(true);
    this.dialogRef.close({
      completed: true,
      progress: 100,
      currentTime: this.player?.duration ?? 0,
    } as VideoModalResult);
  }

  private closeWithProgress(): void {
    this.trackEvent(EventTypes.VIDEO_CLOSE, {
      progress_pct: Math.round(this.lastProgress),
      current_time_seconds: Math.round(this.lastCurrentTime),
    });
    this.saveProgress(false);
    this.dialogRef.close({
      completed: false,
      progress: this.lastProgress,
      currentTime: this.lastCurrentTime,
    } as VideoModalResult);
  }

  private trackEvent(type: EventTypes, extra: Record<string, any> = {}): void {
    this.eventService.pushNew(type, {
      claim_id: this.data.claimId,
      video_index: this.data.videoIndex,
      video_url: this.data.video.url,
      ...extra,
    }).catch(() => { /* non-blocking */ });
  }

  private saveProgress(completed: boolean): void {
    if (!this.data.storageKey) return;
    const payload = {
      progress: completed ? 100 : this.lastProgress,
      currentTime: completed ? 0 : this.lastCurrentTime,
      completed,
    };
    try {
      localStorage.setItem(this.data.storageKey, JSON.stringify(payload));
    } catch (_) { /* quota exceeded */ }
  }

  private loadProgress(): { progress: number; currentTime: number; completed: boolean } | null {
    if (!this.data.storageKey) return null;
    try {
      const raw = localStorage.getItem(this.data.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }
}
